import { useCallback, useEffect } from 'react';
import { Room, RoomEvent, TrackPublication, Participant, Track } from 'livekit-client';
import {
  IntegrityEventType,
  useStartAiInterviewIntegrityMonitoringSessionMutation,
} from 'generated/graphql';
import {
  IntegrityMonitoringHandle,
  StartIntegritySession,
  useIntegrityMonitoring,
} from 'hooks/useIntegrityMonitoring';

type HookProps = {
  room: Room | null;
  aiInterviewCandidateId: string;
};

// AI-interview wrapper around the owner-agnostic integrity monitoring core:
// monitoring runs while the candidate is connected to the interview room,
// sessions attach to the AI-interview action candidate, and the LiveKit
// camera mute state feeds the CAMERA_TURNED_OFF signal (held open until the
// camera comes back).
export function useAiInterviewIntegrityMonitoring({
  room,
  aiInterviewCandidateId,
}: HookProps): IntegrityMonitoringHandle {
  const [startMutation] =
    useStartAiInterviewIntegrityMonitoringSessionMutation();

  const enabled =
    !!room && room.state === 'connected' && !!aiInterviewCandidateId;

  const startSession = useCallback<StartIntegritySession>(
    async (input) => {
      const { data } = await startMutation({
        variables: {
          aiInterviewAssessmentStageActionCandidateId: aiInterviewCandidateId,
          input,
        },
      });
      return data?.startAiInterviewIntegrityMonitoringSession ?? null;
    },
    [aiInterviewCandidateId, startMutation],
  );

  const handle = useIntegrityMonitoring({ enabled, startSession });
  const { openEvent, closeEvent } = handle;

  useEffect(() => {
    if (!room || !enabled) return;

    const handleTrackMuted = (
      publication: TrackPublication,
      participant: Participant,
    ) => {
      if (!participant.isLocal) return;
      if (publication.source !== Track.Source.Camera) return;
      openEvent(IntegrityEventType.CameraTurnedOff);
    };

    const handleTrackUnmuted = (
      publication: TrackPublication,
      participant: Participant,
    ) => {
      if (!participant.isLocal) return;
      if (publication.source !== Track.Source.Camera) return;
      closeEvent(IntegrityEventType.CameraTurnedOff);
    };

    room.on(RoomEvent.TrackMuted, handleTrackMuted);
    room.on(RoomEvent.TrackUnmuted, handleTrackUnmuted);
    return () => {
      room.off(RoomEvent.TrackMuted, handleTrackMuted);
      room.off(RoomEvent.TrackUnmuted, handleTrackUnmuted);
    };
  }, [room, enabled, openEvent, closeEvent]);

  return handle;
}
